import {
  DASHBOARD_GRAPH_AUCTIONS,
  DASHBOARD_GRAPH_BUYERS,
  DashboardGraphID,
  IDashboardHomeCardMetricKey,
} from '~/src/dash/api/dashboards/home';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import { IDashboardChartMetric } from '~/src/dash/components/charts/common';
import {
  formatSelectedBidderGroupIntoStringOfIDs,
  formatSelectedByJoiningIDsWithCommas,
  formatSelectedChannelGroupIntoStringOfIDs,
  formatSelectedDestinationsIntoStringOfSiteIDs,
  formatSelectedRouteIntoStringOfSiteIDs,
  formatSelectedToStringWithEscapes,
  formatSelectedToUpperCaseStringWithCommasAndEscapes,
  ICustomAPIFilterConfig,
} from '~/src/dash/components/filters/common';
import {
  IListViewTableColumns,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  CurrencyRender,
  EllipsisWithTooltipRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { EDateGroup } from '~/src/dash/utils/dateGroups';
import { DateRange } from '~/src/dash/utils/dateTimeRanges';
import {
  clearedActiveFilters,
  IActiveFilters,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';

export interface IDashboardSubTable {
  columns: IListViewTableColumns;
}

export interface IDashboardTopDemandSourcesTable extends IDashboardSubTable {
  activeMetricKeys: string[];
}

export interface IDashboardHomeCardsConfig {
  groupings: Record<DashboardGraphID, string[]>;
  superUserGroupings: Record<DashboardGraphID, string[]>;
  // Samsung specific metrics for second ad call
  samsungAdjustedMetrics: Record<DashboardGraphID, string[]>;
  metrics: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
}

export interface IDashboardOverviewConfig {
  defaultState: {
    activeFilters: IActiveFilters;
    dateRange: DateRange;
    graphID: DashboardGraphID;
    grouping: number;
    isCompareEnabled: boolean;
    timezone?: string;
    displayedMetricKeys: IDashboardHomeCardMetricKey[];
    activeChartMetrics: IDashboardChartMetric[];
    isPeriodMenuOpen: boolean;
    isFilterMenuOpen: boolean;
    tableRowsPerPage: RowsPerPage;
    isDemandSourceUsingBidderType: boolean;
  };
  orderedFilterKeys: string[];
  filters: ICustomAPIFilterConfig;
  cards: IDashboardHomeCardsConfig;
  tables: {
    topChannels: IDashboardSubTable;
    topDemandSources: IDashboardTopDemandSourcesTable;
  };
}

// IMPORTANT: THIS FILE IS SHARED WITH LIVE DASH
// IMPORTANT: PLEASE READ THE README BEFORE EDITING -> https://publica-project.atlassian.net/wiki/x/DQDXpQ

export const dashboardOverviewConfig: IDashboardOverviewConfig = {
  defaultState: {
    activeFilters: clearedActiveFilters,
    dateRange: 'Last 7 Days',
    graphID: DASHBOARD_GRAPH_AUCTIONS,
    grouping: EDateGroup.ByDay,
    isCompareEnabled: true,
    isPeriodMenuOpen: true,
    isFilterMenuOpen: false,
    displayedMetricKeys: [
      'Revenue',
      'Impressions',
      'CPM',
      'EndpointRequests',
      'RenderRate',
      'PodFillRate',
    ],
    activeChartMetrics: [
      {
        key: 'Revenue',
        label: 'Revenue',
        chartColorIndex: 0,
        chartType: 'bar',
        valueType: 'currency',
        yScale: 'base',
      },
    ],
    tableRowsPerPage: 10,
    isDemandSourceUsingBidderType: false,
  },
  orderedFilterKeys: [
    'Filtered Demand Sources',
    'Bidders',
    'Bidder Groups',
    'Channels',
    'Channel Groups',
    'Routes',
    'Destinations',
    'Device Types',
  ],
  filters: {
    'Filtered Demand Sources': {
      tKey: 'DemandSources',
      queryKey: 'biddercodes',
      formatRequestValue: formatSelectedToStringWithEscapes,
      category: 'demand',
      isGroup: true,
    },
    Bidders: {
      tKey: 'Bidders',
      queryKey: 'bidder_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      limit: 100,
      displayID: true,
      category: 'demand',
      isActiveStatusEnabled: true,
    },
    'Bidder Groups': {
      tKey: 'BidderGroups',
      queryKey: 'bidder_ids',
      formatRequestValue: formatSelectedBidderGroupIntoStringOfIDs,
      limit: 1,
      displayID: true,
      category: 'demand',
      isGroup: true,
      isSubset: true,
    },
    Channels: {
      tKey: 'Channels',
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      limit: 20,
      displayID: true,
      category: 'supply',
      isActiveStatusEnabled: true,
    },
    'Channel Groups': {
      tKey: 'ChannelGroups',
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedChannelGroupIntoStringOfIDs,
      limit: 1,
      displayID: true,
      category: 'supply',
      isGroup: true,
      isSubset: true,
    },
    Routes: {
      tKey: 'Routes',
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedRouteIntoStringOfSiteIDs,
      limit: 1,
      category: 'supply',
      isSubset: true,
    },
    Destinations: {
      tKey: 'Destinations',
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedDestinationsIntoStringOfSiteIDs,
      limit: 20,
      category: 'supply',
      isSubset: true,
    },
    'Device Types': {
      tKey: 'DeviceTypes',
      queryKey: 'devicetypes',
      formatRequestValue: formatSelectedToUpperCaseStringWithCommasAndEscapes,
      category: 'device',
    },
    // CUSTOM CASE FOR LIVE ONLY. IF YOU NEED TO ALTER THIS, PLEASE SEARCH FOR INSTANCES ON BOTH OVERVIEW AND LIVE
    'Outbound Deals': {
      tKey: 'OutboundDeals',
      queryKey: 'deal_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      limit: 20,
      displayID: true,
      category: 'deal',
      isSubset: true,
    },
  } as ICustomAPIFilterConfig,
  cards: {
    groupings: {
      [DASHBOARD_GRAPH_AUCTIONS]: [
        'Revenue',
        'Impressions',
        'CPM',
        'EndpointRequests',
        'RenderRate',
        'RenderedPodFillRate',
        'PodFillRate',
        'Auctions',
        'CPMPerSecond',
        'EndpointRequestsECPM',
        'FillRate',
        'PodClearRate',
        'PaidPodClearRate',
      ],
      [DASHBOARD_GRAPH_BUYERS]: [
        'Revenue',
        'Impressions',
        'CPM',
        'WinningBids',
        'RenderRate',
        'BidRequests',
        'BidResponses',
        'BidResponseRate',
        'BidTimeoutRate',
        'CPMPerSecond',
        'BidWinRate',
      ],
    },
    superUserGroupings: {
      [DASHBOARD_GRAPH_AUCTIONS]: [
        'BidSaverTrueLift',
        'BidSaverImpressions',
        'BidSaverWinningBids',
      ],
      [DASHBOARD_GRAPH_BUYERS]: ['BidSaverTrueLift', 'BidSaverImpressions', 'BidSaverWinningBids'],
    },
    samsungAdjustedMetrics: {
      [DASHBOARD_GRAPH_AUCTIONS]: [
        'AdjustedFillRate',
        'AdjustedPodFillRate',
        'AdjustedPodClearRate',
        'AdjustedPaidPodClearRate',
        'AdjustedEndpointRequests',
      ],
      [DASHBOARD_GRAPH_BUYERS]: [],
    },
    metrics: {
      Revenue: {
        key: 'Revenue',
        label: 'Revenue',
        responseKey: 'Revenue',
        trendKey: 'RevenueTrend',
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
      Impressions: {
        key: 'Impressions',
        label: 'Impressions',
        responseKey: 'Impressions',
        trendKey: 'ImpressionsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      CPM: {
        key: 'CPM',
        label: 'CPM',
        responseKey: 'CPM',
        trendKey: 'CPMTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      CPMPerSecond: {
        key: 'CPMPerSecond',
        label: 'CPM Per Second',
        responseKey: 'CPMPerSecond',
        trendKey: 'CPMPerSecondTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      RenderRate: {
        key: 'RenderRate',
        label: 'Render Rate',
        responseKey: 'RenderRate',
        trendKey: 'RenderRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      EndpointRequests: {
        key: 'EndpointRequests',
        label: 'Endpoint Requests',
        responseKey: 'EndpointRequests',
        trendKey: 'EndpointRequestsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      EndpointRequestsECPM: {
        key: 'EndpointRequestsECPM',
        label: 'Endpoint Requests eCPM',
        responseKey: 'EndpointRequestsECPM',
        trendKey: 'EndpointRequestsECPMTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      Auctions: {
        key: 'Auctions',
        label: 'Auctions',
        responseKey: 'Auctions',
        trendKey: 'AuctionsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      RenderedPodFillRate: {
        key: 'RenderedPodFillRate',
        label: 'Rendered Pod Fill Rate',
        responseKey: 'RenderedPodFillRate',
        trendKey: 'RenderedPodFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      AdjustedFillRate: {
        key: 'AdjustedFillRate',
        label: 'Adjusted Fill Rate',
        responseKey: 'AdjustedFillRate',
        trendKey: 'AdjustedFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      AdjustedPodFillRate: {
        key: 'AdjustedPodFillRate',
        label: 'Adjusted Pod Fill Rate',
        responseKey: 'AdjustedPodFillRate',
        trendKey: 'AdjustedPodFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      AdjustedPodClearRate: {
        key: 'AdjustedPodClearRate',
        label: 'Adjusted Pod Clear Rate',
        responseKey: 'AdjustedPodClearRate',
        trendKey: 'AdjustedPodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      AdjustedPaidPodClearRate: {
        key: 'AdjustedPaidPodClearRate',
        label: 'Adjusted Paid Pod Clear Rate',
        responseKey: 'AdjustedPaidPodClearRate',
        trendKey: 'AdjustedPaidPodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      AdjustedEndpointRequests: {
        key: 'AdjustedEndpointRequests',
        label: 'Adjusted Endpoint Requests',
        responseKey: 'AdjustedEndpointRequests',
        trendKey: 'AdjustedEndpointRequestsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      PodFillRate: {
        key: 'PodFillRate',
        label: 'Pod Fill Rate',
        responseKey: 'PodFillRate',
        trendKey: 'PodFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      PodClearRate: {
        key: 'PodClearRate',
        label: 'Pod Clear Rate',
        responseKey: 'PodClearRate',
        trendKey: 'PodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      PaidPodClearRate: {
        key: 'PaidPodClearRate',
        label: 'Paid Pod Clear Rate',
        responseKey: 'PaidPodClearRate',
        trendKey: 'PaidPodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      FillRate: {
        key: 'FillRate',
        label: 'Fill Rate',
        responseKey: 'FillRate',
        trendKey: 'FillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidRequests: {
        key: 'BidRequests',
        label: 'Bid Requests',
        responseKey: 'BidRequests',
        trendKey: 'BidRequestsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidResponses: {
        key: 'BidResponses',
        label: 'Bid Responses',
        responseKey: 'BidResponses',
        trendKey: 'BidResponsesTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      WinningBids: {
        key: 'WinningBids',
        label: 'Winning Bids',
        responseKey: 'WinningBids',
        trendKey: 'WinningBidsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidWinRate: {
        key: 'BidWinRate',
        label: 'Bid Win Rate',
        responseKey: 'BidWinRate',
        trendKey: 'BidWinRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidResponseRate: {
        key: 'BidResponseRate',
        label: 'Bid Response Rate',
        responseKey: 'BidResponseRate',
        trendKey: 'BidResponseRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidTimeoutRate: {
        isInverseTrend: true,
        key: 'BidTimeoutRate',
        label: 'Bid Timeout Rate',
        responseKey: 'BidTimeoutRate',
        trendKey: 'BidTimeoutRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      EndpointBlockedEndpointShield: {
        key: 'EndpointBlockedEndpointShield',
        label: 'Endpoint Block - Endpoint Shield',
        responseKey: 'EndpointBlockedEndpointShield',
        trendKey: 'EndpointBlockedEndpointShieldTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidSaverTrueLift: {
        key: 'BidSaverTrueLift',
        label: '* Publica Vault True Lift',
        responseKey: 'BidSaverTrueLift',
        trendKey: 'BidSaverTrueLiftTrend',
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
      BidSaverImpressions: {
        key: 'BidSaverImpressions',
        label: '* Publica Vault Impressions',
        responseKey: 'BidSaverImpressions',
        trendKey: 'BidSaverImpressionsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidSaverWinningBids: {
        key: 'BidSaverWinningBids',
        label: '* Publica Vault Winning Bids',
        responseKey: 'BidSaverWinningBids',
        trendKey: 'BidSaverWinningBidsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
    } as Record<IDashboardHomeCardMetricKey, IDashboardCard>,
  },
  tables: {
    topChannels: {
      columns: {
        // IMPORTANT: IF YOU UPDATE THIS, MAKE SURE TO ALSO UPDATE THE FUNCTION getActiveColumnKeys IN TOPCHANNELSTABLE
        ID: {
          formatValue: (value: number) => value.toString(),
          isHidden: true,
          isSearchable: true,
          key: 'ID',
          label: 'ID',
          requestKey: 'SiteID',
          responseKey: 'SiteID',
          valueType: 'string',
        },
        Name: {
          cellJSX: EllipsisWithTooltipRender,
          cellRenderType: 'name_link',
          defaultOrderDirection: 'asc',
          headerRenderType: 'name',
          isSearchable: true,
          key: 'Name',
          label: 'Channel',
          tKey: 'Channel',
          orderByKey: 'site_name',
          requestKey: 'SiteName',
          responseKey: 'SiteName',
          valueType: 'string',
        },
        Revenue: {
          cellJSX: CurrencyRender,
          cellRenderType: 'number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'Revenue',
          label: 'Revenue',
          orderByKey: 'revenue',
          requestKey: '',
          responseKey: 'Revenue',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        Impressions: {
          cellRenderType: 'abbreviated_number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'Impressions',
          label: 'Imps',
          orderByKey: 'impressions',
          requestKey: '',
          responseKey: 'Impressions',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        CPM: {
          cellJSX: CurrencyRender,
          cellRenderType: 'number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'CPM',
          label: 'CPM',
          orderByKey: 'avg_imp_ecpm',
          requestKey: '',
          responseKey: 'CPM',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        CPMPerSecond: {
          cellJSX: CurrencyRender,
          cellRenderType: 'number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'CPMPerSecond',
          label: 'CPM/Second',
          orderByKey: 'cpm_per_second',
          requestKey: '',
          responseKey: 'CPMPerSecond',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        RenderRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'RenderRate',
          label: 'Render Rate',
          orderByKey: 'render_rate',
          requestKey: '',
          responseKey: 'RenderRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        EndpointRequests: {
          cellRenderType: 'abbreviated_number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'EndpointRequests',
          label: 'Requests',
          orderByKey: 'endpoint_requests',
          requestKey: '',
          responseKey: 'EndpointRequests',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        FillRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'FillRate',
          label: 'Fill Rate',
          orderByKey: 'fillrate',
          requestKey: '',
          responseKey: 'FillRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        RenderedPodFillRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'RenderedPodFillRate',
          label: 'Rendered Pod Fill Rate',
          orderByKey: 'rendered_pod_fill_rate',
          requestKey: '',
          responseKey: 'RenderedPodFillRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        PodFillRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'PodFillRate',
          label: 'Pod Fill Rate',
          orderByKey: 'pod_fill_rate',
          requestKey: '',
          responseKey: 'PodFillRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        PodClearRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'PodClearRate',
          label: 'Pod Clear Rate',
          orderByKey: 'pod_clear_rate',
          requestKey: '',
          responseKey: 'PodClearRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        PaidPodClearRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'PaidPodClearRate',
          label: 'Paid Pod Clear Rate',
          orderByKey: 'paid_pod_clear_rate',
          requestKey: '',
          responseKey: 'PaidPodClearRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
      },
    },
    topDemandSources: {
      activeMetricKeys: [
        'Revenue',
        'Impressions',
        'CPM',
        'CPMPerSecond',
        'RenderRate',
        'BidResponseRate',
        'BidTimeoutRate',
      ],
      columns: {
        ID: {
          formatValue: (value: number) => value.toString(),
          isHidden: true,
          isSearchable: true,
          key: 'ID',
          label: 'ID',
          requestKey: 'BidderType',
          responseKey: 'BidderType',
          valueType: 'string',
        },
        Name: {
          cellJSX: EllipsisWithTooltipRender,
          cellRenderType: 'name_capitalize',
          defaultOrderDirection: 'asc',
          headerRenderType: 'name',
          isSearchable: true,
          key: 'Name',
          label: 'Demand Source',
          tKey: 'DemandSource',
          orderByKey: 'bidder_type',
          requestKey: 'BidderType',
          responseKey: 'BidderType',
          valueType: 'string',
        },
        Revenue: {
          cellJSX: CurrencyRender,
          cellRenderType: 'number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'Revenue',
          label: 'Revenue',
          orderByKey: 'revenue',
          requestKey: '',
          responseKey: 'Revenue',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        Impressions: {
          cellRenderType: 'abbreviated_number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'Impressions',
          label: 'Imps',
          orderByKey: 'impressions',
          requestKey: '',
          responseKey: 'Impressions',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        CPM: {
          cellJSX: CurrencyRender,
          cellRenderType: 'number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'CPM',
          label: 'CPM',
          orderByKey: 'avg_imp_ecpm',
          requestKey: '',
          responseKey: 'CPM',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        CPMPerSecond: {
          cellJSX: CurrencyRender,
          cellRenderType: 'number',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'CPMPerSecond',
          label: 'CPM/Second',
          orderByKey: 'cpm_per_second',
          requestKey: '',
          responseKey: 'CPMPerSecond',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        RenderRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'RenderRate',
          label: 'Render Rate',
          orderByKey: 'render_rate',
          requestKey: '',
          responseKey: 'RenderRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        BidResponseRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'BidResponseRate',
          label: 'Bid Resp Rate',
          orderByKey: 'bid_response_rate',
          requestKey: '',
          responseKey: 'BidResponseRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
        BidTimeoutRate: {
          cellRenderType: 'percent',
          defaultOrderDirection: 'desc',
          headerRenderType: 'number',
          isDefaultActive: true,
          isNumeric: true,
          key: 'BidTimeoutRate',
          label: 'Bid Timeout Rate',
          orderByKey: 'bid_timeouts_rate',
          requestKey: '',
          responseKey: 'BidTimeoutRate',
          sortIcon: 'arrow_down',
          valueType: 'number',
        },
      },
    },
  },
};

export const getDashboardOverviewConfig = (
  isSuperUser: boolean,
  isSamsungPublisher: boolean,
): IDashboardOverviewConfig => {
  if (isSuperUser) {
    // Auction
    (dashboardOverviewConfig?.cards?.superUserGroupings[DASHBOARD_GRAPH_AUCTIONS] || []).forEach(
      (superUserGrouping: string) => {
        (dashboardOverviewConfig?.cards?.groupings[DASHBOARD_GRAPH_AUCTIONS] || []).push(
          superUserGrouping,
        );
      },
    );
    // Buyer
    (dashboardOverviewConfig?.cards?.superUserGroupings[DASHBOARD_GRAPH_BUYERS] || []).forEach(
      (superUserGrouping: string) => {
        (dashboardOverviewConfig?.cards?.groupings[DASHBOARD_GRAPH_BUYERS] || []).push(
          superUserGrouping,
        );
      },
    );
  }
  if (isSamsungPublisher) {
    // Auction
    (
      dashboardOverviewConfig?.cards?.samsungAdjustedMetrics[DASHBOARD_GRAPH_AUCTIONS] || []
    ).forEach((samsungGrouping: string) => {
      (dashboardOverviewConfig?.cards?.groupings[DASHBOARD_GRAPH_AUCTIONS] || []).push(
        samsungGrouping,
      );
    });
  }
  return dashboardOverviewConfig;
};
